import { useState, useEffect } from "react";
import { useLocation, useSearch, Link } from "wouter";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Zap, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SEO } from "@/components/seo";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { registerSchema, loginSchema } from "@shared/models/auth";

type LoginForm = z.infer<typeof loginSchema>;
type RegisterForm = z.infer<typeof registerSchema>;

export default function AuthPage() {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [, navigate] = useLocation();
  const searchString = useSearch();
  const redirectTo = new URLSearchParams(searchString).get("redirect") || "/";
  const { toast } = useToast();
  const { login, register, isLoggingIn, isRegistering, user } = useAuth();
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);

  const loginForm = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const registerForm = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: { email: "", password: "", firstName: "", lastName: "" },
  });

  useEffect(() => {
    if (user) {
      navigate(redirectTo);
    }
  }, [user, navigate, redirectTo]);

  if (user) {
    return null;
  }

  async function handleLogin(data: LoginForm) {
    try {
      await login(data);
      toast({ title: "Welcome back!" });
      navigate(redirectTo);
    } catch (error: any) {
      let description = "Please check your email and password and try again.";
      try {
        const parsed = JSON.parse(error?.message?.replace(/^\d+:\s*/, "") || "{}");
        if (parsed.message?.toLowerCase().includes("invalid")) {
          description = "The email or password you entered is incorrect. Please try again.";
        } else if (parsed.message) {
          description = parsed.message;
        }
      } catch {}
      toast({ title: "Unable to sign in", description, variant: "destructive" });
    }
  }

  async function handleRegister(data: RegisterForm) {
    try {
      await register(data);
      toast({ title: "Account created successfully!" });
      navigate(redirectTo);
    } catch (error: any) {
      let description = "Something went wrong. Please try again.";
      try {
        const parsed = JSON.parse(error?.message?.replace(/^\d+:\s*/, "") || "{}");
        if (parsed.message?.toLowerCase().includes("already exists") || parsed.message?.toLowerCase().includes("already registered")) {
          description = "An account with this email already exists. Try signing in instead.";
        } else if (parsed.message) {
          description = parsed.message;
        }
      } catch {}
      toast({ title: "Unable to create account", description, variant: "destructive" });
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO
        title={mode === "login" ? "Sign In" : "Create Account"}
        description={mode === "login" ? "Sign in to your Sierra Chart Pro account." : "Create a Sierra Chart Pro account to start shopping."}
        noIndex
      />

      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
              <Zap className="w-6 h-6 text-primary-foreground" />
            </div>
          </div>
          <h1 className="text-2xl font-bold" data-testid="text-auth-title">
            {mode === "login" ? "Sign In" : "Create Account"}
          </h1>
          <p className="text-muted-foreground mt-2">
            {mode === "login"
              ? "Sign in to access your purchases and checkout."
              : "Create an account to start shopping."}
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            {mode === "login" ? (
              <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    data-testid="input-login-email"
                    {...loginForm.register("email")}
                  />
                  {loginForm.formState.errors.email && (
                    <p className="text-sm font-medium text-destructive">{loginForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showLoginPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      className="pr-10"
                      autoComplete="current-password"
                      data-testid="input-login-password"
                      {...loginForm.register("password")}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      tabIndex={-1}
                      data-testid="button-toggle-login-password"
                    >
                      {showLoginPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && (
                    <p className="text-sm font-medium text-destructive">{loginForm.formState.errors.password.message}</p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Link href="/forgot-password" className="text-sm text-primary hover:underline" data-testid="link-forgot-password">
                    Forgot password?
                  </Link>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoggingIn}
                  data-testid="button-login-submit"
                >
                  {isLoggingIn ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing In...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={registerForm.handleSubmit(handleRegister)} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-firstname">First Name</Label>
                    <Input
                      id="register-firstname"
                      placeholder="John"
                      autoComplete="given-name"
                      data-testid="input-register-firstname"
                      {...registerForm.register("firstName")}
                    />
                    {registerForm.formState.errors.firstName && (
                      <p className="text-sm font-medium text-destructive">{registerForm.formState.errors.firstName.message}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-lastname">Last Name</Label>
                    <Input
                      id="register-lastname"
                      placeholder="Doe"
                      autoComplete="family-name"
                      data-testid="input-register-lastname"
                      {...registerForm.register("lastName")}
                    />
                    {registerForm.formState.errors.lastName && (
                      <p className="text-sm font-medium text-destructive">{registerForm.formState.errors.lastName.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-email">Email</Label>
                  <Input
                    id="register-email"
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    data-testid="input-register-email"
                    {...registerForm.register("email")}
                  />
                  {registerForm.formState.errors.email && (
                    <p className="text-sm font-medium text-destructive">{registerForm.formState.errors.email.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="register-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="register-password"
                      type={showRegisterPassword ? "text" : "password"}
                      placeholder="At least 8 characters"
                      className="pr-10"
                      autoComplete="new-password"
                      data-testid="input-register-password"
                      {...registerForm.register("password")}
                    />
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                      tabIndex={-1}
                      data-testid="button-toggle-register-password"
                    >
                      {showRegisterPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {registerForm.formState.errors.password && (
                    <p className="text-sm font-medium text-destructive">{registerForm.formState.errors.password.message}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isRegistering}
                  data-testid="button-register-submit"
                >
                  {isRegistering ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center text-sm">
              {mode === "login" ? (
                <p className="text-muted-foreground">
                  Don't have an account?{" "}
                  <button
                    type="button"
                    onClick={() => { setMode("register"); setShowLoginPassword(false); }}
                    className="text-primary font-medium hover:underline"
                    data-testid="link-switch-to-register"
                  >
                    Create one
                  </button>
                </p>
              ) : (
                <p className="text-muted-foreground">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => { setMode("login"); setShowRegisterPassword(false); }}
                    className="text-primary font-medium hover:underline"
                    data-testid="link-switch-to-login"
                  >
                    Sign in
                  </button>
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
